import { Inject } from '@odinjs/odin';
import { FetchAPI, FilterAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeSubmit, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FragmentNames, RespiratoryMonitoringAliases } from '../../codegen';
import { DateFilterUtils } from '../../utils/date-filter-utils';
import BaseController from '../BaseController';
import InformationBarUtils from '../commons/header/information-bar-header-utils';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../constants/routes';
import ShowToast from '../../utils/toast-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { VitalSignRootService } from '../vital-sign/vital-sign-respiratory-monitoring/vital-sign-root-service';
import { ItemPepType, RulesItemConfiguration } from '../../constants/utils-pep';
import { Endpoint } from '../../constants/endpoints';
import { YesNoType } from '../../utils/enum-utils';

interface FilterParam {
  dtFinal: Date;
  dtInicial: Date;
}

interface FilterParams {
  finalDate: Date;
  initialDate: Date;
}

@Controller({ fragment: FragmentNames.RESPIRATORY_MONITORING })
class RespiratoryMonitoringController extends BaseController {

  initialDate!: string;
  finalDate!: string;
  internalNavigation!: boolean;

  @Inject(RespiratoryMonitoringAliases.FETCH_VITAL_SIGN)
  private fetchDataApi!: FetchAPI;

  @Inject(RespiratoryMonitoringAliases.PANEL_GLOBAL_ACTIONS)
  private panelGlobalActionsApi!: PanelAPI;

  @OnMount({ target: RespiratoryMonitoringAliases.RESPIRATORY_MONITORING_LAYOUT })
  handleLayoutMount(): void {
    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({encounterId, patientId});

    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && VitalSignRootService.isAllowEdit;
        this.panelGlobalActionsApi.setVisible(allowInfo);
      })

  }

  @OnUnmount({ target: RespiratoryMonitoringAliases.RESPIRATORY_MONITORING_LAYOUT })
  handleLayoutUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnMount({ target: RespiratoryMonitoringAliases.RESPIRATORY_MONITORING_FILTER })
  handleFilterMount(event: MountEvent): void {

    let record = {
      finalDate: '',
      initialDate: '',
    };

    const filter = event.target as FilterAPI;
    const restored = filter.restoreState(true);

    if (!restored) {
      const {
        lastMonth,
        currentDate
      } = DateFilterUtils.getDateRange();

      record = {
        finalDate: currentDate,
        initialDate: lastMonth,
      };

    } else {
      Object.assign(record, filter.getRecord());
    }

    filter.setData(record);
    this.initialDate = record.initialDate;
    this.finalDate = record.finalDate;

    filter.submit();

  }

  @OnUnmount({ target: RespiratoryMonitoringAliases.RESPIRATORY_MONITORING_FILTER })
  handleFilterUnmount(event: MountEvent): void {
    const filter = event.target as FilterAPI;

    if ( this.internalNavigation ) {
      const record = filter.getRecord() as Record<string, FilterParam>;
      filter.setData(record);
      filter.persistState();
    } else {
      filter.clearPersistedState();
    }
  }

  @OnBeforeSubmit({ target: RespiratoryMonitoringAliases.RESPIRATORY_MONITORING_FILTER })
  handleBeforeSubmit(event: SubmitEvent): void {

    const params = {
      finalDate: event.record.finalDate,
      initialDate: event.record.initialDate
    } as FilterParams;

    if(DateFilterUtils.diffDate(params.finalDate, params.initialDate) > 90) {
      ShowToast.open(MessageService.translate('text.message.more-ninety-days'), ToastLevel.CAUTION);
    }

    this.fetchDataApi.setParameters(params as unknown as Record<string, FilterParams>);
  }

  @OnClick({target: RespiratoryMonitoringAliases.DECK_VITAL_SIGN })
  handleCardClick(event: ClickEvent): void {
    const { sequenceNumber, releaseDate } = event.value;
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && VitalSignRootService.isAllowEdit;
    const accessViewer = releaseDate || !allowInfo;
    const route = accessViewer ? ApplicationRoute.RESPIRATORY_MONITORING_VIEWER : ApplicationRoute.RESPIRATORY_MONITORING_EDITOR;

    this.internalNavigation = true;

    applicationContext.redirect(route, {
      encounterId,
      patientId,
      sequenceNumber
    });
  }

  @OnClick({ target: RespiratoryMonitoringAliases.BUTTON_ADD_RESPIRATORY_MONITORING })
  handleNewClick(): void {
    applicationContext.redirect(ApplicationRoute.RESPIRATORY_MONITORING_CREATE);
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.VITAL_SIGN;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        VitalSignRootService.isAllowEdit = isAllowEdit;
      })
  }

}

export default RespiratoryMonitoringController;
